import {
  compareAsc,
  formatDuration,
  isSameDay,
  isDate,
  isValid,
} from 'date-fns'
import { StrictMode } from 'react'
import type { VFC } from 'react'
import { offsetFromUserTimeZone, timeZoneName } from '@opallabs/concepts'
import { styles } from './styles'
import type { DateInfoPanelProps } from './types'

/**
 * Explain the difference between the user’s time zone and `targetZone`.
 */
export const DateInfoPanel: VFC<DateInfoPanelProps> = ({
  locale = navigator.language,
  startDateTime,
  targetZone,
}) => {
  const theme = 'dark'

  if (!isDate(startDateTime) || !isValid(startDateTime)) return null

  const userZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (userZone === targetZone) return null

  const targetZoneName = timeZoneName(startDateTime, targetZone, { locale })
  const userZoneName = timeZoneName(startDateTime, userZone, { locale })
  const rawOffset = offsetFromUserTimeZone(startDateTime, targetZone)

  const offsetValue = rawOffset[0] * 60 + rawOffset[1]
  if (offsetValue === 0) return null

  const adjective = offsetValue < 0 ? 'behind' : 'ahead of'

  // @note `formatDuration` takes a `locale` argument, but using it requires
  //   data bundles from `date-fns/locale`. We didn't like shipping data
  //   bundles for MomentJS so we're not doing it with `date-fns`.
  const humanDifference = formatDuration({
    hours: Math.abs(rawOffset[0]),
    minutes: rawOffset[1],
  })

  let suffix = ''
  let contentVerb = 'is'
  let zoneVerb: string
  const now = new Date()
  const dateRelativeToNow = compareAsc(startDateTime, now)
  if (isSameDay(startDateTime, now) || dateRelativeToNow === 0) {
    zoneVerb = 'is'
  } else if (dateRelativeToNow === 1) {
    zoneVerb = 'will be'
    suffix = ' on the day it’s scheduled'
  } else {
    contentVerb = 'was'
    zoneVerb = 'was'
    suffix = ' on the scheduled date'
  }

  return (
    <StrictMode>
      <p css={[styles.text, styles.textColor(theme)]}>
        This content {contentVerb} planned in {targetZoneName.long}, which{' '}
        {zoneVerb}{' '}
        <em css={[styles.difference, styles.highlightedText(theme)]}>
          {humanDifference} {adjective}
        </em>{' '}
        {userZoneName.long}
        {suffix}.
      </p>
    </StrictMode>
  )
}
DateInfoPanel.displayName = 'Photon: DateInfoPanel'
